import { i18n } from 'i18n';
import { waterBinderRatioFromConcentrationAndBinder } from 'modules/project/ProjectAreaPage/ProjectStopesPage/canvas/PCFunctions/PCOtherFunctions';
import { DictionaryRange } from 'modules/project/ProjectAreaPage/ProjectStopesPage/MineModel/types';
import {
  CustomUnitFields,
  DisplayUnits,
  FillType,
  HydraulicBackfillSpecificationDto,
  HydraulicRecipeSpecificationDto,
  MetricPressureUnit,
  MixerCoefficientsDto,
  PasteBackfillSpecificationDto,
  PasteRecipeSpecificationDto,
  ThroughputControlType,
  UnitSystem
} from 'providers/api';
import { generateMassBalanceFromFlowRate, generateMassBalanceFromTonnage } from 'utils';
import {
  batchMixerPower,
  continuousMixerPower,
  resolveSlump,
  resolveYieldStress
} from 'utils/backfill';
import { isNumber, round } from 'utils/number';
import {
  binderTonnesImperialToMetric,
  binderTonnesMetricToImperial,
  densityImperialToMetric,
  densityMetricToImperial,
  diameterImperialToMetric,
  diameterMetricToImperial,
  distanceImperialToMetric,
  distanceMetricToImperial,
  flowRateImperialToMetric,
  frictionLossImperialToMetric,
  frictionLossMetricToImperial,
  flowRateMetricToImperial,
  gravityImperialToMetric,
  gravityMetricToImperial,
  isCustomUnitField,
  lengthImperialToMetric,
  lengthMetricToImperial,
  powerImperialToMetric,
  powerMetricToImperial,
  pressureImperialToMetric,
  pressureMetricToImperial,
  pumpPressureImperialToMetric,
  pumpPressureKpaToBar,
  pumpPressureMetricToImperial,
  smallDistanceImperialToMetric,
  smallDistanceMetricToImperial,
  smallWeightImperialToMetric,
  smallWeightMetricToImperial,
  speedImperialToMetric,
  speedMetricToImperial,
  tonnageImperialToMetric,
  tonnageMetricToImperial,
  volumeImperialToMetric,
  volumeMetricToImperial,
  weightImperialToMetric,
  weightMetricToImperial
} from 'utils/unitConversions';

const CONTINUOUS_MIXER_KEYS = ['continuousMixerCoefficientM', 'continuousMixerCoefficientC', 'continuousMixerCoefficientN', 'continuousMixerCoefficientD'];
const BATCH_MIXER_KEYS = ['batchMixerCoefficientM', 'batchMixerCoefficientN', 'batchMixerCoefficientD'];

const hasNullOrUndefinedValues = (object: any, keys: string[]) => keys.some((key) => object[key] === null || object[key] === undefined);

// INFO: false positive
export enum BasicDisplayUnits {
  PressureUCS = 0,
  Density = 10,
  DryTonnage = 20,
  WetTonnage = 25,
  Distance = 30,
  Volume = 40,
  Speed = 50,
  PressurePump = 60,
  Length = 70,
  Weight = 80,
  smallWeight = 85,
  Diameter = 90,
  SmallDistance = 100,
  PressureSmall = 110,
  Percentage = 120,
  Gravity = 130,
  LargeWeight = 140,
}

export type UnitOfMeasurement = {
  abbreviation: string,
  conversion: (currentValue: number) => number,
  decimalPlaces?: number;
}

export interface UnitBase {
  name: string;
  // The project CustomUnitFields member that inverts this unit's metric/imperial display —
  // the same gate the server's note generation uses. Undefined = not invertible; the unit
  // always follows the project's base unit system.
  customUnitField?: CustomUnitFields;
  warning?: string;
  decimalPlaces: number;
  metric: UnitOfMeasurement;
  imperial: UnitOfMeasurement;
}

export interface UnitBaseWithValue extends UnitBase {
  value: number | undefined;
}

// eslint-disable-next-line import/prefer-default-export
export const additionalUnitLabels: Record<DisplayUnits, UnitBase> = {
  [DisplayUnits.DrySolidFeedRate]: {
    name: i18n._('Dry Tonnage'),
    customUnitField: CustomUnitFields.DrySolidFeedRate,
    warning: i18n._('Dry tonnage requires a value for solids throughput'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 't/h',
      conversion: tonnageImperialToMetric,
    },
    imperial: {
      abbreviation: 'tn/h',
      conversion: tonnageMetricToImperial,
    },
  },

  [DisplayUnits.SlurryFeedRate]: {
    name: i18n._('Slurry Tonnage'),
    customUnitField: CustomUnitFields.SlurryFeedRate,
    warning: i18n._('Slurry tonnage requires a value for specific gravity'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 't/h',
      conversion: tonnageImperialToMetric,
    },
    imperial: {
      abbreviation: 'tn/h',
      conversion: tonnageMetricToImperial,
      decimalPlaces: 1,
    },
  },

  [DisplayUnits.FlowRate]: {
    name: i18n._('Flow Rate'),
    customUnitField: CustomUnitFields.FlowRate,
    warning: i18n._('Flow rate requires a value for specific gravity'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'm\u00B3/h',
      conversion: flowRateImperialToMetric,
    },
    imperial: {
      abbreviation: 'gpm',
      conversion: flowRateMetricToImperial,
    },
  },

  [DisplayUnits.SolidsDensity]: {
    name: i18n._('Solids Density'),
    customUnitField: CustomUnitFields.SolidsDensity,
    warning: i18n._('Solids density requires a value for specific gravity'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'kg/m\u00B3',
      conversion: densityImperialToMetric,
    },
    imperial: {
      abbreviation: 'lb/ft\u00B3',
      conversion: densityMetricToImperial,
    },
  },

  [DisplayUnits.SlurryDensity]: {
    name: i18n._('Slurry Density'),
    customUnitField: CustomUnitFields.SlurryDensity,
    warning: i18n._('Slurry density requires a value for specific gravity'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'kg/m\u00B3',
      conversion: densityImperialToMetric,
    },
    imperial: {
      abbreviation: 'lb/ft\u00B3',
      conversion: densityMetricToImperial,
    },
  },

  [DisplayUnits.ContinuousMixerPowerDraw]: {
    name: i18n._('Cont. Mixer Power'),
    customUnitField: CustomUnitFields.ContinuousMixerPowerDraw,
    warning: i18n._('Continuous Mixer Power requires values for specificGravity, yieldStressA, yieldStressB, and all continuous mixer coefficients'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'kW',
      conversion: powerImperialToMetric,
    },
    imperial: {
      abbreviation: 'hp',
      conversion: powerMetricToImperial,
    },
  },

  [DisplayUnits.BatchMixerPowerDraw]: {
    name: i18n._('Bat. Mixer Power'),
    customUnitField: CustomUnitFields.BatchMixerPowerDraw,
    warning: i18n._('Batch Mixer Power requires values for specificGravity, yieldStressA, yieldStressB, and all batch mixer coefficients'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'kW',
      conversion: powerImperialToMetric,
    },
    imperial: {
      abbreviation: 'hp',
      conversion: powerMetricToImperial,
    },
  },

  [DisplayUnits.Slump]: {
    name: i18n._('Slump'),
    customUnitField: CustomUnitFields.Slump,
    warning: i18n._('slump requires values for specificGravity, yieldStressA, and yieldStressB'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'mm',
      conversion: smallDistanceImperialToMetric,
    },
    imperial: {
      abbreviation: 'in',
      conversion: smallDistanceMetricToImperial,
    },
  },

  [DisplayUnits.YieldStress]: {
    name: i18n._('Yield Stress'),
    warning: i18n._('yield stress requires values for massConcentration, yieldStressA, and yieldStressB'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'Pa',
      conversion: (currentNumber: number) => currentNumber,
    },
    imperial: {
      abbreviation: 'Pa',
      conversion: (currentNumber: number) => currentNumber,
    },
  },

  [DisplayUnits.WBRatio]: {
    name: i18n._('W:B Ratio'),
    warning: i18n._('W:B Ratio requires values for totalDryCentrationByMass and binderConcentration'),
    decimalPlaces: 1,
    metric: {
      abbreviation: '',
      conversion: (currentNumber: number) => currentNumber,
    },
    imperial: {
      abbreviation: '',
      conversion: (currentNumber: number) => currentNumber,
    },
  },
  [DisplayUnits.BinderTonnes]: {
    name: i18n._('Mass of Binder'),
    customUnitField: CustomUnitFields.BinderTonnes,
    decimalPlaces: 0,
    metric: {
      abbreviation: 't/h',
      conversion: binderTonnesImperialToMetric,
    },
    imperial: {
      abbreviation: 'tn/h',
      conversion: binderTonnesMetricToImperial,
    },
  },
  [DisplayUnits.FrictionLoss]: {
    name: i18n._('FrictionLoss'),
    // The server gates friction loss (kPa/m vs psi/ft) with the Strength field.
    customUnitField: CustomUnitFields.Strength,
    decimalPlaces: 1,
    metric: {
      abbreviation: 'kPa/m',
      conversion: frictionLossImperialToMetric,
    },
    imperial: {
      abbreviation: 'psi/ft',
      conversion: frictionLossMetricToImperial,
    },
  },
};

export const unitLabels: Record<BasicDisplayUnits, UnitBase> = {
  [BasicDisplayUnits.Volume]: {
    name: i18n._('Volume'),
    customUnitField: CustomUnitFields.PourVolume,
    decimalPlaces: 0,
    metric: {
      abbreviation: 'm\u00B3',
      conversion: volumeImperialToMetric,
    },
    imperial: {
      abbreviation: 'ft\u00B3',
      conversion: volumeMetricToImperial,
    },
  },

  [BasicDisplayUnits.Distance]: {
    name: i18n._('Distance'),
    customUnitField: CustomUnitFields.PipeLength,
    decimalPlaces: 0,
    metric: {
      abbreviation: 'm',
      conversion: distanceImperialToMetric,
    },
    imperial: {
      abbreviation: 'ft',
      conversion: distanceMetricToImperial,
    },
  },

  [BasicDisplayUnits.SmallDistance]: {
    name: i18n._('Wear'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'mm',
      conversion: smallDistanceImperialToMetric,
    },
    imperial: {
      abbreviation: 'in',
      conversion: smallDistanceMetricToImperial,
    },
  },

  [BasicDisplayUnits.WetTonnage]: {
    name: i18n._('Wet Tonnage'),
    customUnitField: CustomUnitFields.WetTonnage,
    decimalPlaces: 0,
    metric: {
      abbreviation: 't/h',
      conversion: tonnageImperialToMetric,
    },
    imperial: {
      abbreviation: 'tn/h',
      conversion: tonnageMetricToImperial,
    },
  },

  [BasicDisplayUnits.DryTonnage]: {
    name: i18n._('Dry Tonnage'),
    customUnitField: CustomUnitFields.DryTonnage,
    decimalPlaces: 0,
    metric: {
      abbreviation: 't/h',
      conversion: tonnageImperialToMetric,
    },
    imperial: {
      abbreviation: 'tn/h',
      conversion: tonnageMetricToImperial,
    },
  },

  [BasicDisplayUnits.Density]: {
    name: i18n._('Density'),
    decimalPlaces: 3,
    metric: {
      abbreviation: 'kg/m\u00B3',
      conversion: densityImperialToMetric,
    },
    imperial: {
      abbreviation: 'lb/ft\u00B3',
      conversion: densityMetricToImperial,
    },
  },

  [BasicDisplayUnits.PressureUCS]: {
    name: i18n._('Strength'),
    customUnitField: CustomUnitFields.Strength,
    decimalPlaces: 0,
    metric: {
      abbreviation: 'kPa',
      conversion: pressureImperialToMetric,
    },
    imperial: {
      abbreviation: 'psi',
      conversion: pressureMetricToImperial,
    },
  },

  [BasicDisplayUnits.PressurePump]: {
    name: i18n._('Pump Pressure'),
    customUnitField: CustomUnitFields.PumpPressure,
    decimalPlaces: 1,
    metric: {
      abbreviation: 'bar',
      conversion: pumpPressureImperialToMetric,
    },
    imperial: {
      abbreviation: 'psi',
      conversion: pumpPressureMetricToImperial,
    },
  },

  [BasicDisplayUnits.Gravity]: {
    name: i18n._('Gravity'),
    customUnitField: CustomUnitFields.Gravity,
    decimalPlaces: 1,
    metric: {
      abbreviation: 'm head',
      conversion: gravityImperialToMetric,
    },
    imperial: {
      abbreviation: 'ft head',
      conversion: gravityMetricToImperial,
    },
  },

  [BasicDisplayUnits.Speed]: {
    name: i18n._('Velocity'),
    customUnitField: CustomUnitFields.Velocity,
    decimalPlaces: 1,
    metric: {
      abbreviation: 'm/s',
      conversion: speedImperialToMetric,
    },
    imperial: {
      abbreviation: 'ft/s',
      conversion: speedMetricToImperial,
    },
  },

  [BasicDisplayUnits.Length]: {
    name: i18n._('Length'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'cm',
      conversion: lengthImperialToMetric,
    },
    imperial: {
      abbreviation: 'in',
      conversion: lengthMetricToImperial,
    },
  },

  [BasicDisplayUnits.Weight]: {
    name: i18n._('Weight'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 'kg',
      conversion: weightImperialToMetric,
    },
    imperial: {
      abbreviation: 'lb',
      conversion: weightMetricToImperial,
    },
  },
  [BasicDisplayUnits.smallWeight]: {
    name: i18n._('Small Weight'),
    decimalPlaces: 1,
    metric: {
      abbreviation: 'g',
      conversion: smallWeightImperialToMetric,
    },
    imperial: {
      abbreviation: 'oz',
      conversion: smallWeightMetricToImperial,
    },
  },
  [BasicDisplayUnits.Diameter]: {
    name: i18n._('Diameter'),
    decimalPlaces: 1,
    metric: {
      abbreviation: 'mm',
      conversion: diameterImperialToMetric,
    },
    imperial: {
      abbreviation: 'in',
      conversion: diameterMetricToImperial,
    },
  },
  [BasicDisplayUnits.PressureSmall]: {
    name: i18n._('Pressure Small'),
    decimalPlaces: 1,
    metric: {
      abbreviation: 'Pa',
      conversion: (currentNumber: number) => currentNumber,
    },
    imperial: {
      abbreviation: 'Pa',
      conversion: (currentNumber: number) => currentNumber,
    },
  },
  [BasicDisplayUnits.Percentage]: {
    name: i18n._('Percentage'),
    decimalPlaces: 1,
    metric: {
      abbreviation: '%',
      conversion: (currentNumber: number) => currentNumber * 100,
    },
    imperial: {
      abbreviation: '%',
      conversion: (currentNumber: number) => currentNumber * 100,
    },
  },
  [BasicDisplayUnits.LargeWeight]: {
    name: i18n._('Large Weight'),
    decimalPlaces: 0,
    metric: {
      abbreviation: 't',
      conversion: weightImperialToMetric,
    },
    imperial: {
      abbreviation: 'tn',
      conversion: weightMetricToImperial,
    },
  },
};

/**
 * Unit abbreviations double as lingui msgids so catalogues for non-Latin alphabets (ru, gr) can
 * re-letter them (e.g. "psi" -> "пси"); Latin-alphabet catalogues keep them verbatim. The value is
 * exposed through a getter so the active locale is honoured at access time rather than at module
 * load: the tables above are built once, but the user can switch language at any point.
 */
export const localiseUnitOfMeasurement = <T extends UnitOfMeasurement>(unit: T): T => {
  const msgid = unit.abbreviation;
  Object.defineProperty(unit, 'abbreviation', {
    get: () => (msgid ? i18n._(msgid) : msgid),
    enumerable: true,
    configurable: true,
  });
  return unit;
};

export const localiseUnitBase = <T extends UnitBase>(unit: T): T => {
  localiseUnitOfMeasurement(unit.metric);
  localiseUnitOfMeasurement(unit.imperial);
  return unit;
};

Object.values(additionalUnitLabels).forEach(localiseUnitBase);
Object.values(unitLabels).forEach(localiseUnitBase);

// Registers every unit abbreviation with `lingui extract` (extraction is static, so each literal
// must appear inside an i18n._() call). Not meant to be called; the getters above do the work.
export const unitAbbreviationMsgIds = () => [
  i18n._('t/h'), i18n._('tn/h'), i18n._('t'), i18n._('tn'),
  i18n._('m³/h'), i18n._('gpm'), i18n._('m³'), i18n._('ft³'),
  i18n._('kg/m³'), i18n._('lb/ft³'),
  i18n._('kW'), i18n._('hp'),
  i18n._('mm'), i18n._('cm'), i18n._('m'), i18n._('in'), i18n._('ft'),
  i18n._('Pa'), i18n._('kPa'), i18n._('bar'), i18n._('psi'),
  i18n._('kPa/m'), i18n._('psi/ft'),
  i18n._('m head'), i18n._('ft head'),
  i18n._('m/s'), i18n._('ft/s'),
  i18n._('g'), i18n._('kg'), i18n._('oz'), i18n._('lb'),
];

export const getPressurePumpUnitLabel = (metricPressureUnit: MetricPressureUnit): UnitBase => (
  metricPressureUnit === MetricPressureUnit.KiloPascal
    ? {
      ...unitLabels[BasicDisplayUnits.PressurePump],
      decimalPlaces: 0,
      metric: localiseUnitOfMeasurement({ ...unitLabels[BasicDisplayUnits.PressurePump].metric, abbreviation: 'kPa', conversion: pumpPressureKpaToBar }),
    }
    : unitLabels[BasicDisplayUnits.PressurePump]);

export const convertIfRequired = (value: number, unitSystem: UnitSystem, unit: UnitBase, customUnitFields: CustomUnitFields[]) => (
  !isCustomUnitField(unitSystem, customUnitFields, unit.customUnitField)
    ? value
    : unit.imperial.conversion(value));

export const getAbbreviation = (unit: UnitBase, unitSystem: UnitSystem, customUnitFields: CustomUnitFields[]) => (
  !isCustomUnitField(unitSystem, customUnitFields, unit.customUnitField)
    ? unit.metric.abbreviation
    : unit.imperial.abbreviation);

export const convertValueToImperialdsfdsf = (unit: UnitBase, value: number, unitSystem: UnitSystem) => (unitSystem === UnitSystem.Metric
  ? value
  : unit.imperial.conversion(value));

export interface AdditionalDisplayUnitsOutput {
  additionalDisplayUnits: Record<DisplayUnits, UnitBaseWithValue>;
  warnings: Record<DisplayUnits, number | undefined>;
}

export interface AdditionalDisplayUnitsInputs {
  key: string;
  displayUnitPreferences: DisplayUnits[];
  throughputControlType: ThroughputControlType;
  specification?: PasteBackfillSpecificationDto | HydraulicBackfillSpecificationDto;
  selectedSpecification?: PasteRecipeSpecificationDto | HydraulicRecipeSpecificationDto;

  // Rheology data
  tailingsSolidsDensity?: number,
  binderParticleDensity?: number,
  carrierFluidDensity?: number,
  specificGravity?: number;
  yieldStressA?: number;
  yieldStressB?: number;
  mixerCoefficients?: MixerCoefficientsDto;
  heightOfCylinder?: number;
  fillType: FillType;
  limits?: DictionaryRange;
}

const resolveContinuousMixerPowerDraw = (slump?: number, slurryFeedRate?: number, mixerCoefficients?: MixerCoefficientsDto) => {
  if (!slump || !slurryFeedRate || !mixerCoefficients || hasNullOrUndefinedValues(mixerCoefficients, CONTINUOUS_MIXER_KEYS)) return undefined;
  return continuousMixerPower(
    slump,
    slurryFeedRate,
    mixerCoefficients.continuousMixerCoefficientM!,
    mixerCoefficients.continuousMixerCoefficientC!,
    mixerCoefficients.continuousMixerCoefficientN!,
    mixerCoefficients.continuousMixerCoefficientD!,
  );
};

const resolveBatchMixerPowerDraw = (slump?: number, slurryFeedRate?: number, mixerCoefficients?: MixerCoefficientsDto) => {
  if (!slump || !slurryFeedRate || !mixerCoefficients || hasNullOrUndefinedValues(mixerCoefficients, BATCH_MIXER_KEYS)) return undefined;
  return batchMixerPower(
    slump,
    slurryFeedRate,
    mixerCoefficients.continuousMixerCoefficientM!,
    mixerCoefficients.continuousMixerCoefficientN!,
    mixerCoefficients.continuousMixerCoefficientD!,
  );
};

const getThroughput = (throughputFromSpec?: number, defaultThroughputFromLimits?: number) => {
  if (throughputFromSpec) return throughputFromSpec;
  if (defaultThroughputFromLimits) return defaultThroughputFromLimits;
  return 0;
};

const useDisplayUnits = (inputs: AdditionalDisplayUnitsInputs[]): Record<string, AdditionalDisplayUnitsOutput> => inputs.reduce((results, info) => {
  const throughput = getThroughput(info.specification?.throughput, info.limits?.tons.default);
  const binderTailingsRatio = info.specification ? (info.specification as HydraulicBackfillSpecificationDto).binderTailingsRatio : 0;
  const pasteBinderContent = info.selectedSpecification ? (info.selectedSpecification as PasteRecipeSpecificationDto).binderContent : 0;
  const binderConcentration = info.fillType === FillType.Paste
    ? pasteBinderContent
    : binderTailingsRatio / (1 + binderTailingsRatio);

  const massBalanceInputs = {
    totalTonnage: throughput,
    totalDryConcentrationByMass: info.selectedSpecification?.massConcentration ?? 0,
    binderConcentration,
    tailingsSolidsDensity: info.tailingsSolidsDensity ?? 0,
    binderParticleDensity: info.binderParticleDensity ?? 0,
    carrierFluidDensity: info.carrierFluidDensity ?? 0,
  };
  let massBalance;

  switch (info.throughputControlType) {
    case ThroughputControlType.DryTonnage:
      massBalance = generateMassBalanceFromTonnage({ ...massBalanceInputs, isDryTonnage: true });
      break;
    case ThroughputControlType.WetTonnage:
      massBalance = generateMassBalanceFromTonnage({ ...massBalanceInputs, isDryTonnage: false });
      break;
    case ThroughputControlType.FlowRate:
      massBalance = generateMassBalanceFromFlowRate({ ...massBalanceInputs, totalFlowRate: throughput });
      break;
    default:
      throw new Error(i18n._('Unable to generate mass balance as this project has no associated fill type'));
  }

  const yieldStress = resolveYieldStress(info.yieldStressA, info.yieldStressB, info.selectedSpecification?.massConcentration);
  const slump = resolveSlump(yieldStress, massBalance?.slurryDensity, info.heightOfCylinder);
  const continuousMixerPowerDraw = resolveContinuousMixerPowerDraw(slump, massBalance?.totalWetTonnage, info.mixerCoefficients);
  const batchMixerPowerDraw = resolveBatchMixerPowerDraw(slump, massBalance?.totalWetTonnage, info.mixerCoefficients);
  const wbRatio = waterBinderRatioFromConcentrationAndBinder(massBalanceInputs.totalDryConcentrationByMass / 100, massBalanceInputs.binderConcentration / 100);

  const additionalDisplayUnits: Record<DisplayUnits, UnitBaseWithValue> = {
    [DisplayUnits.DrySolidFeedRate]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.DrySolidFeedRate) && massBalance?.totalDryTonnage
        ? round(massBalance?.totalDryTonnage)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.DrySolidFeedRate],
    },
    [DisplayUnits.SlurryFeedRate]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.SlurryFeedRate) && massBalance?.totalWetTonnage
        ? round(massBalance?.totalWetTonnage)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.SlurryFeedRate],
    },
    [DisplayUnits.FlowRate]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.FlowRate) && massBalance?.totalFlowRate
        ? round(massBalance?.totalFlowRate)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.FlowRate],
    },
    [DisplayUnits.SolidsDensity]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.SolidsDensity) && massBalance?.totalDrySolidsDensity
        ? round(massBalance?.totalDrySolidsDensity)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.SolidsDensity],
    },
    [DisplayUnits.SlurryDensity]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.SlurryDensity) && massBalance?.slurryDensity
        ? round(massBalance?.slurryDensity)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.SlurryDensity],
    },
    [DisplayUnits.BatchMixerPowerDraw]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.BatchMixerPowerDraw) && batchMixerPowerDraw
        ? round(batchMixerPowerDraw)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.BatchMixerPowerDraw],
    },
    [DisplayUnits.ContinuousMixerPowerDraw]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.ContinuousMixerPowerDraw) && continuousMixerPowerDraw
        ? round(continuousMixerPowerDraw)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.ContinuousMixerPowerDraw],
    },
    [DisplayUnits.Slump]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.Slump) && slump
        ? round(slump)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.Slump],
    },
    [DisplayUnits.YieldStress]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.YieldStress) && yieldStress
        ? round(yieldStress)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.YieldStress],
    },
    [DisplayUnits.WBRatio]: {
      value: info.displayUnitPreferences.includes(DisplayUnits.WBRatio) && wbRatio
        ? round(wbRatio)
        : undefined,
      ...additionalUnitLabels[DisplayUnits.WBRatio],
    },
    [DisplayUnits.BinderTonnes]: {
      // Binder tonnes is not computable from the recipe inputs here (it needs a pour volume),
      // so the hook never carries a value for it — the old `&& 0` ternary always yielded undefined.
      value: undefined,
      ...additionalUnitLabels[DisplayUnits.BinderTonnes],
    },
    [DisplayUnits.FrictionLoss]: {
      value: undefined,
      ...additionalUnitLabels[DisplayUnits.FrictionLoss],
    },
  };

  // create display warnings for any values that should be displayed and that don't have a number value
  const warnings: Record<DisplayUnits, string | undefined> = {
    [DisplayUnits.DrySolidFeedRate]: !info.displayUnitPreferences.includes(DisplayUnits.DrySolidFeedRate) || isNumber(massBalance?.totalDryTonnage)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.DrySolidFeedRate].warning,
    [DisplayUnits.SlurryFeedRate]: !info.displayUnitPreferences.includes(DisplayUnits.SlurryFeedRate) || isNumber(massBalance?.totalWetTonnage)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.SlurryFeedRate].warning,
    [DisplayUnits.FlowRate]: !info.displayUnitPreferences.includes(DisplayUnits.FlowRate) || isNumber(massBalance?.totalFlowRate)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.FlowRate].warning,
    [DisplayUnits.SolidsDensity]: !info.displayUnitPreferences.includes(DisplayUnits.SolidsDensity) || isNumber(massBalance?.totalDrySolidsDensity)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.SolidsDensity].warning,
    [DisplayUnits.SlurryDensity]: !info.displayUnitPreferences.includes(DisplayUnits.SlurryDensity) || isNumber(massBalance?.slurryDensity)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.SlurryDensity].warning,
    [DisplayUnits.BatchMixerPowerDraw]: !info.displayUnitPreferences.includes(DisplayUnits.BatchMixerPowerDraw) || isNumber(batchMixerPowerDraw)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.BatchMixerPowerDraw].warning,
    [DisplayUnits.ContinuousMixerPowerDraw]: !info.displayUnitPreferences.includes(DisplayUnits.ContinuousMixerPowerDraw)
      || isNumber(continuousMixerPowerDraw)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.ContinuousMixerPowerDraw].warning,
    [DisplayUnits.Slump]: !info.displayUnitPreferences.includes(DisplayUnits.Slump) || isNumber(slump)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.Slump].warning,
    [DisplayUnits.YieldStress]: !info.displayUnitPreferences.includes(DisplayUnits.YieldStress) || isNumber(yieldStress)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.YieldStress].warning,
    [DisplayUnits.WBRatio]: !info.displayUnitPreferences.includes(DisplayUnits.WBRatio) || isNumber(wbRatio)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.WBRatio].warning,
    [DisplayUnits.BinderTonnes]: !info.displayUnitPreferences.includes(DisplayUnits.BinderTonnes) || isNumber(0)
      ? undefined
      : additionalDisplayUnits[DisplayUnits.BinderTonnes].warning,
    [DisplayUnits.FrictionLoss]: undefined,
  };

  return {
    ...results,
    [info.key]: { additionalDisplayUnits, warnings },
  };
},
  {});

export default useDisplayUnits;
